import { SelectV2, SelectV2Option } from '@cimplico/components';
import { useEffect, useMemo, useState } from 'react';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useEntityAccounts } from '../../queries/use-coa';
import { EntityAccount } from '../../types/entity';

type AccountSelectProps = {
    size?: 'small' | 'standard';
    title?: string;
    isOptional?: boolean;
    isMulti?: boolean;
    isDisabled?: boolean;
    placeholder?: string;
    errorMessage?: string;
    selectedAccounts?: EntityAccount[];
    disabledAccountIds?: string[];
    onChange: (worksheets: EntityAccount[]) => void;
};

function AccountSelect({
    size,
    title,
    isMulti,
    isOptional,
    isDisabled,
    placeholder,
    errorMessage,
    selectedAccounts = [],
    disabledAccountIds = [],
    onChange,
}: AccountSelectProps): JSX.Element {
    const [selected, setSelected] = useState<SelectV2Option[]>([]);
    const [accountSearch, setAccountSearch] = useState<string>('');
    const { entity, provider } = useWorkpaperContext();

    const accountsQuery = useEntityAccounts(entity.id, entity.charts.find((c) => c.provider?.toString() === provider?.toString())!.id, {
        sort: { dir: 'asc', field: 'code' },
        filter: {
            canJournal: true,
        },
        search: accountSearch,
    });

    const allOptions: SelectV2Option[] = useMemo(() => {
        const allOptions: SelectV2Option[] = (accountsQuery.data?.items || []).map((option) => ({
            value: option.id,
            label: `${option.code} - ${option.name}`,
            isDisabled: disabledAccountIds.includes(option.id),
            disabledHint: "You can't post a journal to this account.",
        }));
        return allOptions;
    }, [accountsQuery.data?.items]);

    function handleAccountSelect(options: SelectV2Option[]): void {
        onChange(
            accountsQuery.data?.items?.filter((w) => {
                return options.find((value) => value.value === w.id);
            }) ?? []
        );
    }

    useEffect(() => {
        const defaultSelected: SelectV2Option[] = selectedAccounts.map((val) => {
            return { value: val.id, label: `${val.code} - ${val.name}` };
        });
        setSelected(defaultSelected);
    }, [selectedAccounts]);

    return (
        <div>
            <SelectV2
                keepSelectedOptions
                placeholder={placeholder || 'Select accounts...'}
                title={title}
                size={size}
                isOptional={isOptional}
                isMulti={isMulti}
                isLoading={accountsQuery.isFetching || accountsQuery.isLoading}
                options={allOptions}
                errorMessage={errorMessage}
                onSearch={setAccountSearch}
                onChange={handleAccountSelect}
                disabled={isDisabled}
                value={selected}
                borderStyle="underline"
                allowOverflow
            />
        </div>
    );
}
export default AccountSelect;
